import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import apiClient from '../api/client';
import { parseApiErrorMessage } from '../utils/apiError';
import {
  normalizePhoneInput,
  validateEmailCom,
  validatePhone10Digits,
} from '../utils/formValidation';
import PhoneInput from '../components/PhoneInput';
import PageTemplate from './PageTemplate';
import './ModulePage.css';

function EditStudent() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [form, setForm] = useState({
    full_name: '',
    email_id: '',
    phone_number: '',
    password: '',
    confirm_password: '',
    has_purchased: false,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function loadStudent() {
      setLoading(true);
      setError('');
      try {
        const res = await apiClient.get(`/api/users/${id}/`);
        if (cancelled) return;

        const student = res.data?.user || res.data;
        setForm({
          full_name: student.full_name || student.name || '',
          email_id: student.email_id || student.email || '',
          phone_number: student.phone_number || student.phone || '',
          password: '',
          confirm_password: '',
          has_purchased: Boolean(student.has_purchased),
        });
      } catch (e) {
        if (!cancelled) {
          if (e?.response?.status === 404) {
            setError('Student not found.');
            setTimeout(() => navigate('/students'), 2000);
          } else {
            setError(
              e?.response?.data?.detail ||
                e?.response?.data?.message ||
                'Unable to load student.'
            );
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    if (id) loadStudent();
    return () => {
      cancelled = true;
    };
  }, [id, navigate]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const nextValue =
      name === 'phone_number' ? normalizePhoneInput(value) : value;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : nextValue,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!form.full_name.trim()) {
      setError('Full name is required.');
      return;
    }

    const emailError = validateEmailCom(form.email_id);
    if (emailError) {
      setError(emailError);
      return;
    }

    const phoneError = validatePhone10Digits(form.phone_number);
    if (phoneError) {
      setError(phoneError);
      return;
    }

    if (form.password || form.confirm_password) {
      if (form.password.length < 8) {
        setError('Password must be at least 8 characters long.');
        return;
      }
      if (form.password !== form.confirm_password) {
        setError('Passwords do not match.');
        return;
      }
    }

    setSubmitting(true);
    try {
      const payload = {
        full_name: form.full_name.trim(),
        email_id: form.email_id.trim(),
        phone_number: normalizePhoneInput(form.phone_number),
        role: 'student',
        has_purchased: Boolean(form.has_purchased),
      };
      if (form.password) {
        payload.password = form.password;
        payload.confirm_password = form.confirm_password;
      }

      await apiClient.put(`/api/users/${id}/`, payload);
      setSuccess('Student updated successfully.');
      setTimeout(() => navigate('/students'), 600);
    } catch (err) {
      setError(
        parseApiErrorMessage(err, 'Unable to update student. Please try again.')
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <PageTemplate title="Edit Student" subtitle="Update student details">
        <p style={{ textAlign: 'center', padding: '40px' }}>Loading student details…</p>
      </PageTemplate>
    );
  }

  return (
    <PageTemplate title="Edit Student" subtitle="Update student account details">
      <div className="add-teacher-panel">
        <form className="add-teacher-grid" onSubmit={handleSubmit} noValidate>
          <div className="form-group">
            <label htmlFor="full_name">Full Name</label>
            <input
              id="full_name"
              name="full_name"
              type="text"
              value={form.full_name}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="email_id">Email</label>
            <input
              id="email_id"
              name="email_id"
              type="email"
              placeholder="name@example.com"
              value={form.email_id}
              onChange={handleChange}
              required
            />
          </div>
          <PhoneInput
            id="phone_number"
            label="Mobile Number"
            value={form.phone_number}
            onChange={(phone) => setForm((prev) => ({ ...prev, phone_number: phone }))}
            required
            disabled={submitting}
          />
          <div className="form-group">
            <label htmlFor="password">New Password (optional)</label>
            <input
              id="password"
              name="password"
              type="password"
              placeholder="Leave blank to keep current"
              value={form.password}
              onChange={handleChange}
            />
          </div>
          <div className="form-group">
            <label htmlFor="confirm_password">Confirm New Password</label>
            <input
              id="confirm_password"
              name="confirm_password"
              type="password"
              value={form.confirm_password}
              onChange={handleChange}
            />
          </div>
          <div className="form-group form-group-wide">
            <label
              className="form-checkbox-label"
              style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}
            >
              <input
                type="checkbox"
                name="has_purchased"
                checked={form.has_purchased}
                onChange={handleChange}
              />
              <span>Has purchased course</span>
            </label>
          </div>

          {error && <p className="form-error form-group-wide">{error}</p>}
          {success && <p className="form-success form-group-wide">{success}</p>}

          <div className="add-teacher-actions">
            <button
              type="button"
              className="btn-primary btn-secondary"
              onClick={() => navigate('/students')}
            >
              Cancel
            </button>
            <button type="submit" className="primary-btn" disabled={submitting}>
              {submitting ? 'Updating…' : 'Update Student'}
            </button>
          </div>
        </form>
      </div>
    </PageTemplate>
  );
}

export default EditStudent;
